// src/hooks/useWorkspaces.js
import { useEffect, useState, useCallback } from "react";
import { workspacesService } from "../services/workspacesService";

export const useWorkspaces = ({ token }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadWorkspaces = useCallback(async () => {
    // if (!token) return;
    setLoading(true);
    try {
      const response = await workspacesService.getAll(token);
      // Integration tests show data is in response.data.data
      const actualData = (response?.data || []).filter(w => w);
      setWorkspaces(actualData);

      const savedId = localStorage.getItem("workspaceId");
      if (actualData.length > 0) {
        const target = actualData.find(ws => String(ws.id) === String(savedId)) || actualData[0];
        setSelectedWorkspace(target);
        if (target.id) localStorage.setItem("workspaceId", target.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const createWorkspace = async (name, type = "home") => {
    try {
      const ws = await workspacesService.create(token, name, type);
      setWorkspaces((prev) => [ws, ...prev]);
      return ws;
    } catch (err) {
      throw err;
    }
  };

  const updateWorkspace = async (id, data) => {
    try {
      const updated = await workspacesService.update(token, id, data);
      setWorkspaces((prev) => prev.map((w) => (w.id === id ? { ...w, ...updated } : w)));
      if (selectedWorkspace?.id === id) setSelectedWorkspace(prev => ({ ...prev, ...updated }));
      return updated;
    } catch (err) {
      throw err;
    }
  };

  const deleteWorkspace = async (id) => {
    try {
      await workspacesService.delete(token, id);
      setWorkspaces((prev) => {
        const nextList = prev.filter((w) => w.id !== id);
        if (selectedWorkspace?.id === id) {
          setSelectedWorkspace(nextList.length > 0 ? nextList[0] : null);
        }
        return nextList;
      });
    } catch (err) {
      throw err;
    }
  };

  // --- MEMBER ACTIONS ---

  const getMembers = useCallback(async (workspaceId) => {
    setError("");
    try {
      const response = await workspacesService.getMembers(token, workspaceId);
      return response.data || [];
    } catch (err) {
      console.error(err);
      setError("Failed to load notes.");
    } finally {
    }
  }, [token]);

  const addMember = async (workspaceId, memberData) => {
    try {
      // Backend returns a member_status object. 
      // Note: Tests show the response includes profileId and message.
      const response = await workspacesService.addMember(token, workspaceId, memberData);

      // Since backend doesn't return the FULL member object here, 
      // you might want to reload or manually construct the member.
      const newMember = {
        id: response.profileId,
        ...memberData
      };

      const updateState = (ws) => {
        if (ws.id !== workspaceId) return ws;
        return { ...ws, members: [...(ws.members || []), newMember] };
      };

      setWorkspaces((prev) => prev.map(updateState));
      if (selectedWorkspace?.id === workspaceId) setSelectedWorkspace(prev => updateState(prev));
      return response;
    } catch (err) {
      throw err;
    }
  };

  const updateMember = async (workspaceId, profileId, data) => {
    try {
      await workspacesService.updateMember(token, workspaceId, profileId, data);

      const updateState = (ws) => {
        if (ws.id !== workspaceId) return ws;
        return {
          ...ws,
          members: (ws.members || []).map(m => m.id === profileId ? { ...m, ...data } : m)
        };
      };

      setWorkspaces((prev) => prev.map(updateState));
      if (selectedWorkspace?.id === workspaceId) setSelectedWorkspace(prev => updateState(prev));
    } catch (err) {
      throw err;
    }
  };

  const deleteMember = async (workspaceId, profileId) => {
    try {
      await workspacesService.removeMember(token, workspaceId, profileId);
      const updateState = (ws) => {
        if (ws.id !== workspaceId) return ws;
        return { ...ws, members: (ws.members || []).filter(m => m.id !== profileId) };
      };
      setWorkspaces((prev) => prev.map(updateState));
      if (selectedWorkspace?.id === workspaceId) setSelectedWorkspace(prev => updateState(prev));
    } catch (err) {
      throw err;
    }
  };

  // --- INVITE / FEATURES ---

  const inviteMember = async (workspaceId, email, role) => {
    return await workspacesService.createInvite(token, workspaceId, email, role);
  };

  const revokeInvite = async (workspaceId, inviteId) => {
    await workspacesService.deleteInvite(token, workspaceId, inviteId);
  };

  const getInvites = useCallback(async (workspaceId) => {
    try {
      const res = await workspacesService.getInvites(token, workspaceId);
      return res.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [token]);

  const getMyInvites = useCallback(async () => {
    try {
      const res = await workspacesService.getMyInvites(token);
      return res.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [token]);

  const joinWorkspace = async (inviteToken, displayName) => {
    const res = await workspacesService.acceptInvite(token, inviteToken, displayName);
    await loadWorkspaces();
    return res;
  };

  const toggleFeature = async (workspaceId, featureId, state) => {
    try {
      await workspacesService.setFeature(token, workspaceId, featureId, state);
      // Update local state to reflect the feature toggle
      setWorkspaces((prev) => prev.map((w) => {
        if (w.id !== workspaceId) return w;
        const features = (w.features || []).map((f) =>
          f.id === featureId ? { ...f, enabled: !!state } : f
        );
        return { ...w, features };
      }));
      // Sync selected workspace
      if (selectedWorkspace?.id === workspaceId) {
        setSelectedWorkspace(prev => ({
          ...prev,
          features: (prev.features || []).map(f => f.id === featureId ? { ...f, enabled: !!state } : f)
        }));
      }
    } catch (err) {
      console.error("Feature toggle failed", err);
    }
  };

  return {
    workspaces,
    selectedWorkspace,
    loading,
    error,
    setSelectedWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    getMembers,
    addMember,
    updateMember,
    deleteMember,
    inviteMember,
    revokeInvite,
    joinWorkspace,
    toggleFeature,
    getInvites,
    getMyInvites,
    reload: loadWorkspaces,
  };
};